/**
 * Blog Engine - Portfolio v3
 * Dynamically renders blog posts from JSON data
 */

import postsData from './data/posts.json';

export interface Post {
  id: string;
  title: string;
  date: string;
  excerpt: string;
  tags: string[];
  content: string;
}

/** Escape a string for safe interpolation into innerHTML. */
function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (char) => {
    switch (char) {
      case '&': return '&amp;';
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '"': return '&quot;';
      default: return '&#39;';
    }
  });
}

export class BlogEngine {
  private container: HTMLElement | null;
  private posts: Post[] = [];

  constructor(containerId: string) {
    this.container = document.getElementById(containerId);
    this.initHashListener();
  }

  public init(): void {
    if (!this.container) return;
    this.posts = postsData as Post[];
    this.handleRouting();
  }

  private initHashListener(): void {
    window.addEventListener('hashchange', () => this.handleRouting());
  }

  private handleRouting(): void {
    const hash = window.location.hash;
    if (hash.startsWith('#blog/')) {
      const postId = decodeURIComponent(hash.replace('#blog/', ''));
      const post = this.posts.find(p => p.id === postId);
      if (post) {
        this.renderPostDetail(post);
      } else {
        this.renderPosts(this.posts);
      }
    } else if (hash === '#blog') {
      this.renderPosts(this.posts);
    } else {
      // If we are not in blog hash, but the container is populated, 
      // we might want to keep the list ready for when they scroll to the section.
      this.renderPosts(this.posts);
    }
  }

  private renderPosts(posts: Post[]): void {
    if (!this.container) return;
    this.container.innerHTML = '';

    posts.forEach(post => {
      const postEl = document.createElement('article');
      postEl.className = 'glass-card blog-post animate-in';
      postEl.innerHTML = `
        <div class="blog-post__meta mono">
          <span class="token--comment">// ${escapeHtml(post.date)}</span>
          <div class="blog-post__tags">
            ${post.tags.map(tag => `<span class="token--number">#${escapeHtml(tag)}</span>`).join(' ')}
          </div>
        </div>
        <h3 class="mono">${escapeHtml(post.title)}</h3>
        <p>${escapeHtml(post.excerpt)}</p>
        <a href="#blog/${encodeURIComponent(post.id)}" class="mono token--function">read_more()</a>
      `;
      this.container?.appendChild(postEl);
    });
  }

  private renderPostDetail(post: Post): void {
    if (!this.container) return;
    this.container.innerHTML = `
      <div class="glass-card blog-post--detail animate-in">
        <div class="blog-post__meta mono">
          <a href="#blog" class="token--keyword">cd ..</a>
          <span class="token--comment">// ${escapeHtml(post.date)}</span>
        </div>
        <h2 class="mono token--function">${escapeHtml(post.title)}</h2>
        <div class="blog-post__content">
          <p>${escapeHtml(post.content)}</p>
        </div>
        <div class="blog-post__footer">
          <a href="#blog" class="button button--secondary mono">read_all_posts()</a>
        </div>
      </div>
    `;
    
    // Smooth scroll to blog section to ensure they see the content
    this.container.scrollIntoView({ behavior: 'smooth' });
  }
}
